import { useCallback, useRef, useState } from 'react';
import {
    AI_WORKFLOW_STEP_COUNT,
    AI_WORKFLOW_STEP_KEYS,
    type AiWorkflowStepKey,
} from '../utils/aiWorkflowSteps';

export function useAiWorkflowProgress() {
    const [stepIndex, setStepIndex] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const stopAutoAdvance = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const reset = useCallback(() => {
        stopAutoAdvance();
        setStepIndex(0);
    }, [stopAutoAdvance]);

    const advanceTo = useCallback((index: number) => {
        setStepIndex((prev) => Math.min(Math.max(prev, index), AI_WORKFLOW_STEP_COUNT - 1));
    }, []);

    const startAutoAdvance = useCallback((maxStep: number, intervalMs = 2200) => {
        stopAutoAdvance();
        timerRef.current = setInterval(() => {
            setStepIndex((prev) => {
                if (prev >= maxStep) {
                    stopAutoAdvance();
                    return prev;
                }
                return prev + 1;
            });
        }, intervalMs);
    }, [stopAutoAdvance]);

    const stepKey: AiWorkflowStepKey = AI_WORKFLOW_STEP_KEYS[stepIndex];

    return {
        stepIndex,
        stepKey,
        advanceTo,
        reset,
        startAutoAdvance,
        stopAutoAdvance,
    };
}
